import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Send, Trash2, Users, Mail, ChevronDown, ChevronRight, Cake, Moon, UserRound } from "lucide-react";
import { api } from "../lib/api";

const AUDIENCES = [
  { key: "all", label: "Все клиенты", icon: Users, hint: "Все, кто дал согласие на рассылки" },
  { key: "inactive30", label: "Не были 30+ дней", icon: Moon, hint: "Вернуть тех, кто давно не заходил" },
  { key: "inactive60", label: "Не были 60+ дней", icon: Moon, hint: "Совсем «уснувшие» гости" },
  { key: "birthday7", label: "День рождения скоро", icon: Cake, hint: "ДР в ближайшие 7 дней" },
];

const STRATEGY_RU = {
  least_recently_contacted: "Сначала те, кто давно не получал (рекомендуется)",
  most_recently_contacted: "Сначала недавние получатели",
  newest_first: "Сначала новые клиенты",
  oldest_first: "Сначала давние клиенты",
  random: "Случайный порядок",
};

const EMPTY = {
  channel: "telegram", name: "", subject: "", content: "", button_text: "", link: "",
  limit: 100, strategy: "least_recently_contacted", cooldown_days: 3, ignore_cooldown: false,
};

const buildFilters = ({ audience, gender, ageFrom, ageTo }) => {
  const f = [];
  if (audience === "inactive30") f.push({ field: "inactive_days", value: 30 });
  if (audience === "inactive60") f.push({ field: "inactive_days", value: 60 });
  if (audience === "birthday7") f.push({ field: "birthday_within_days", value: 7 });
  if (gender && gender !== "any") f.push({ field: "gender", value: gender });
  if (ageFrom) f.push({ field: "age_min", value: +ageFrom });
  if (ageTo) f.push({ field: "age_max", value: +ageTo });
  return f;
};

function StepTitle({ n, children }) {
  return (
    <div className="flex items-center gap-2.5">
      <span className="w-7 h-7 rounded-full bg-[#FF0000] text-white font-black text-sm flex items-center justify-center shrink-0">{n}</span>
      <span className="text-sm font-extrabold">{children}</span>
    </div>
  );
}

const Chip = ({ active, onClick, children, testid }) => (
  <button type="button" data-testid={testid} onClick={onClick}
    className={`px-3 py-2 rounded-xl border-2 text-xs font-bold transition flex items-center gap-1.5 ${active ? "border-[#FF0000] bg-red-50 text-[#C40000]" : "border-gray-200 bg-white text-gray-600 hover:border-gray-300"}`}>
    {children}
  </button>
);

export default function Broadcasts() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(null);
  const [audience, setAudience] = useState("all");
  const [gender, setGender] = useState("any");
  const [ageFrom, setAgeFrom] = useState("");
  const [ageTo, setAgeTo] = useState("");
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const countTimer = useRef(null);

  const load = () => api.get("/admin/broadcasts").then((r) => setItems(r.data));
  useEffect(() => {
    load();
  }, []);

  const filters = form ? buildFilters({ audience, gender, ageFrom, ageTo }) : [];

  // auto-count recipients (debounced)
  useEffect(() => {
    if (!form) return;
    clearTimeout(countTimer.current);
    countTimer.current = setTimeout(async () => {
      try {
        const { data } = await api.post("/admin/broadcasts/preview", { ...form, filters, logic: "and" });
        setPreview(data);
      } catch (e) { setPreview(null); }
    }, 500);
    return () => clearTimeout(countTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form?.channel, form?.limit, form?.cooldown_days, form?.ignore_cooldown, audience, gender, ageFrom, ageTo, !!form]);

  const ruError = (e) => {
    if (!e) return "";
    if (e.includes("chat not found")) return "чат не найден — клиент не запускал бота";
    if (e.includes("blocked")) return "клиент заблокировал бота";
    return e;
  };
  const notifyResult = (res) => {
    if (res.recipients === 0) { toast.error("Нет получателей: нужны клиенты с согласием на рассылку и с Telegram/email"); return; }
    if (!res.failed) { toast.success(`Отправлено: ${res.sent}`); return; }
    const reason = ruError(res.errors?.[0]?.error);
    toast[res.sent > 0 ? "warning" : "error"](`Отправлено: ${res.sent}, ошибок: ${res.failed}${reason ? ` — ${reason}` : ""}`, { duration: 8000 });
  };

  const create = async (sendNow) => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/broadcasts", { ...form, filters, logic: "and" });
      if (sendNow) {
        const { data: res } = await api.post(`/admin/broadcasts/${data.id}/send`);
        notifyResult(res);
      } else toast.success("Черновик сохранён");
      setForm(null); setPreview(null); load();
    } catch (e) { toast.error("Ошибка"); }
    setBusy(false);
  };
  const send = async (b) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/broadcasts/${b.id}/send`);
      notifyResult(data);
      load();
    } catch (e) { toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Ошибка"); }
    setBusy(false);
  };
  const openForm = () => {
    setForm({ ...EMPTY });
    setAudience("all"); setGender("any"); setAgeFrom(""); setAgeTo("");
    setShowAdvanced(false); setPreview(null);
  };

  return (
    <div className="space-y-4" data-testid="admin-broadcasts">
      <div className="flex items-center justify-between">
        <h1 className="font-display font-black text-2xl">Рассылки</h1>
        {!form && <button data-testid="broadcast-create-btn" className="admin-btn" onClick={openForm}><Send size={15} /> Новая рассылка</button>}
      </div>

      {form && (
        <div className="space-y-3" data-testid="broadcast-form">
          {/* Step 1: audience */}
          <div className="bg-white rounded-2xl p-4 border border-gray-100 space-y-3">
            <StepTitle n="1">Кому отправить</StepTitle>
            <div className="flex gap-2">
              <Chip testid="broadcast-channel-telegram" active={form.channel === "telegram"} onClick={() => setForm({ ...form, channel: "telegram" })}>
                <Send size={13} /> Telegram
              </Chip>
              <Chip testid="broadcast-channel-email" active={form.channel === "email"} onClick={() => setForm({ ...form, channel: "email" })}>
                <Mail size={13} /> Email
              </Chip>
            </div>
            <div className="flex gap-2 flex-wrap">
              {AUDIENCES.map((a) => (
                <Chip key={a.key} testid={`broadcast-audience-${a.key}`} active={audience === a.key} onClick={() => setAudience(a.key)}>
                  <a.icon size={13} /> {a.label}
                </Chip>
              ))}
            </div>
            <p className="text-xs text-gray-400 font-semibold">{AUDIENCES.find((a) => a.key === audience)?.hint}</p>
            <div className="flex flex-wrap gap-4 items-end pt-1">
              <div className="space-y-1">
                <span className="text-xs font-bold text-gray-500 flex items-center gap-1"><UserRound size={12} /> Пол</span>
                <div className="flex gap-1.5">
                  {[["any", "Все"], ["male", "Мужчины"], ["female", "Женщины"]].map(([v, l]) => (
                    <Chip key={v} testid={`broadcast-gender-${v}`} active={gender === v} onClick={() => setGender(v)}>{l}</Chip>
                  ))}
                </div>
              </div>
              <label className="block"><span className="text-xs font-bold text-gray-500">Возраст от</span>
                <input data-testid="broadcast-age-from" type="number" min="0" className="admin-input !w-24" placeholder="18" value={ageFrom} onChange={(e) => setAgeFrom(e.target.value)} /></label>
              <label className="block"><span className="text-xs font-bold text-gray-500">до</span>
                <input data-testid="broadcast-age-to" type="number" min="0" className="admin-input !w-24" placeholder="35" value={ageTo} onChange={(e) => setAgeTo(e.target.value)} /></label>
              <label className="block"><span className="text-xs font-bold text-gray-500">Максимум получателей</span>
                <input data-testid="broadcast-limit-input" type="number" min="1" className="admin-input !w-28" value={form.limit} onChange={(e) => setForm({ ...form, limit: +e.target.value })} /></label>
            </div>
            <div className="flex items-center gap-2 bg-[#FBF3E4] rounded-xl px-3.5 py-2.5">
              <Users size={16} className="text-[#FF0000]" />
              <span className="text-sm font-black" data-testid="broadcast-preview-count">
                {preview ? `Получат сообщение: ${preview.count}` : "Считаем получателей..."}
              </span>
              <span className="text-xs text-gray-400 font-semibold ml-auto">только с согласием на рассылки {form.channel === "email" ? "и с email" : "и с Telegram"}</span>
            </div>
          </div>

          {/* Step 2: message */}
          <div className="bg-white rounded-2xl p-4 border border-gray-100 space-y-3">
            <StepTitle n="2">Сообщение</StepTitle>
            <div className="grid md:grid-cols-2 gap-2">
              <input data-testid="broadcast-name-input" className="admin-input" placeholder="Название рассылки (для себя)" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              {form.channel === "email" && <input data-testid="broadcast-subject-input" className="admin-input" placeholder="Тема письма" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} />}
            </div>
            <textarea data-testid="broadcast-content-input" className="admin-input" rows={4}
              placeholder={"Текст сообщения.\nНапишите {first_name} — подставится имя клиента."}
              value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} />
            <div className="grid md:grid-cols-2 gap-2">
              <input className="admin-input" placeholder="Текст кнопки (необязательно)" value={form.button_text} onChange={(e) => setForm({ ...form, button_text: e.target.value })} />
              <input className="admin-input" placeholder="Ссылка кнопки (https://...)" value={form.link} onChange={(e) => setForm({ ...form, link: e.target.value })} />
            </div>
          </div>

          {/* Step 3: send */}
          <div className="bg-white rounded-2xl p-4 border border-gray-100 space-y-3">
            <StepTitle n="3">Отправка</StepTitle>
            <button type="button" data-testid="broadcast-advanced-toggle" className="flex items-center gap-1 text-xs font-bold text-gray-500" onClick={() => setShowAdvanced(!showAdvanced)}>
              {showAdvanced ? <ChevronDown size={14} /> : <ChevronRight size={14} />} Дополнительные настройки
            </button>
            {showAdvanced && (
              <div className="flex flex-wrap gap-4 items-end bg-gray-50 rounded-xl p-3">
                <label className="block"><span className="text-xs font-bold text-gray-500">Очерёдность отправки</span>
                  <select className="admin-input !w-80" value={form.strategy} onChange={(e) => setForm({ ...form, strategy: e.target.value })}>
                    {Object.entries(STRATEGY_RU).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select></label>
                <label className="block"><span className="text-xs font-bold text-gray-500">Пауза после прошлой рассылки (дней)</span>
                  <input type="number" min="0" className="admin-input !w-24" value={form.cooldown_days} onChange={(e) => setForm({ ...form, cooldown_days: +e.target.value })} /></label>
                <label className="text-xs font-bold flex items-center gap-1.5 pb-2.5">
                  <input type="checkbox" checked={form.ignore_cooldown} onChange={(e) => setForm({ ...form, ignore_cooldown: e.target.checked })} />
                  Отправить даже тем, кто недавно получал
                </label>
              </div>
            )}
            <div className="flex gap-2 items-center flex-wrap">
              <span className="text-sm font-black text-[#FF0000]">{preview ? `${preview.count} получателей` : ""}</span>
              <div className="ml-auto flex gap-2">
                <button className="admin-btn admin-btn-ghost" onClick={() => { setForm(null); setPreview(null); }}>Отмена</button>
                <button data-testid="broadcast-save-draft-btn" className="admin-btn admin-btn-ghost" disabled={busy || !form.name} onClick={() => create(false)}>Сохранить черновик</button>
                <button data-testid="broadcast-send-btn" className="admin-btn admin-btn-red" disabled={busy || !form.name || !form.content} onClick={() => create(true)}><Send size={14} /> Отправить сейчас</button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="text-sm font-extrabold pt-1">История рассылок</div>
      <div className="bg-white rounded-2xl border border-gray-100 overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead><tr className="text-left text-xs text-gray-400 uppercase">
            <th className="px-4 py-3">Название</th><th>Канал</th><th>Статус</th><th>Получатели</th><th>Отправлено</th><th>Ошибки</th><th>Дата</th><th></th>
          </tr></thead>
          <tbody>
            {items.map((b) => (
              <tr key={b.id} className="border-t border-gray-50" data-testid={`broadcast-row-${b.id}`}>
                <td className="px-4 py-2.5 font-bold">{b.name}</td>
                <td><span className={`px-2 py-0.5 rounded-full text-xs font-bold ${b.channel === "email" ? "bg-blue-50 text-blue-600" : "bg-sky-50 text-sky-600"}`}>{b.channel === "email" ? "Email" : "Telegram"}</span></td>
                <td>{b.status === "sent" ? <span className="text-green-600 font-bold text-xs">Отправлена</span> : <span className="text-gray-400 text-xs">Черновик</span>}</td>
                <td>{b.stats?.recipients ?? b.limit}</td>
                <td>{b.stats?.sent ?? 0}</td>
                <td>{b.stats?.failed ?? 0}</td>
                <td className="text-xs text-gray-400">{(b.sent_at || b.created_at)?.slice(0, 16).replace("T", " ")}</td>
                <td className="px-3">
                  <div className="flex gap-1.5">
                    {b.status !== "sent" && <button data-testid={`broadcast-send-now-${b.id}`} className="admin-btn !h-7 text-xs" disabled={busy} onClick={() => send(b)}>Отправить</button>}
                    <button onClick={async () => { await api.delete(`/admin/broadcasts/${b.id}`); load(); }} className="p-1.5 bg-gray-100 rounded-lg text-red-500"><Trash2 size={13} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!items.length && <div className="p-8 text-center text-gray-400 font-bold text-sm">Рассылок пока нет — нажмите «Новая рассылка»</div>}
      </div>
    </div>
  );
}
